/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { shopByCategoryStyles } from "@eisinc/App/Widgets/ShopByCategoryCustom/ShopByCategory.style";
import LazyImage from "@eisinc/MobiusOverrides/LazyImage";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoriesDataView } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography from "@insite/mobius/Typography";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

/* eslint-disable */
const HTMLEllipsis = require("@eisinc/Libraries/react-lines-ellipsis/html.js");
/* eslint-enable */

const enum fields {
    title = "title",
    selectedCategoryIds = "selectedCategoryIds",
    typeOfCategoriesToDisplay = "typeOfCategoriesToDisplay",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.selectedCategoryIds]: string[];
        [fields.title]: string;
        [fields.typeOfCategoriesToDisplay]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    categoriesDataView: getCategoriesDataView(state),
});

const mapDispatchToProps = {
    loadCategories,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const definition: WidgetDefinition = {
    group: "Basic",
    displayName: "Shop By Category - Custom",
    fieldDefinitions: [
        {
            name: fields.title,
            displayName: "Title",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "Translatable",
        },
        {
            name: fields.typeOfCategoriesToDisplay,
            displayName: "Categories To Display",
            editorTemplate: "RadioButtonsField",
            tooltip: "Only first 18 categories will be showing",
            options: [
                {
                    displayName: "Featured Categories",
                    value: "featuredCategories",
                },
                {
                    displayName: "Select Categories",
                    value: "selectCategories",
                },
            ],
            isRequired: true,
            defaultValue: "",
            fieldType: "General",
        },
        {
            name: fields.selectedCategoryIds,
            displayName: "Select Categories",
            editorTemplate: "CategoriesField",
            defaultValue: [],
            fieldType: "General",
            isRequired: true,
            isVisible: widget => widget.fields.typeOfCategoriesToDisplay === "selectCategories",
        },
    ],
};

const gridItemsBreakpoints: any = [6, 4, 3, 2, 2];

const styles = shopByCategoryStyles;

const ShopByCategoryWidget: React.FC<Props> = ({ fields, categoriesDataView, loadCategories }) => {
    const [categories, setCategories] = React.useState([]);
    const [noCategoriesFound, setNoCategoriesFound] = React.useState(false);

    const filterTheCategories = () => {
        let tempCategories: any = [];
        tempCategories.length = 0;
        setNoCategoriesFound(false);
        if (categoriesDataView?.value?.length) {
            if (fields.typeOfCategoriesToDisplay === "featuredCategories") {
                tempCategories = categoriesDataView.value.filter((cat: any) => {
                    return cat["isFeatured"] === true;
                });
            } else if (fields?.selectedCategoryIds?.length) {
                categoriesDataView.value.forEach((cat: any) => {
                    fields.selectedCategoryIds.indexOf(cat.id) > -1 && tempCategories.push(cat);
                });
            }
        }
        !tempCategories.length && setNoCategoriesFound(true);
        setCategories(tempCategories);
    };

    // to avoid memory leak, put the condition inside the useEffect to limit the number of rendering
    React.useEffect(() => {
        if (!categoriesDataView || !categoriesDataView.value) {
            loadCategories();
        }
    }, []);

    React.useEffect(() => {
        filterTheCategories();
    }, [categoriesDataView, fields.selectedCategoryIds, fields.typeOfCategoriesToDisplay]);

    if (categoriesDataView?.isLoading || (!categories.length && !noCategoriesFound)) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    return (
        <>
            {fields.title && (
                <Typography {...styles.TitleText} variant="h3">
                    {fields.title}
                </Typography>
            )}
            {categories.length > 0 && (
                <GridContainer {...styles.container} gap={20}>
                    {categories.slice(0, 24).map(category => (
                        <GridItem {...styles.categoryGridItem} key={uniqueId()} width={gridItemsBreakpoints}>
                            <Link {...styles.categoryLink} href={category["path"]}>
                                <StyledWrapper {...styles.categoryImageWrapper}>
                                    <LazyImage {...styles.categoryImage} src={category["smallImagePath"]} />
                                </StyledWrapper>
                                <StyledWrapper {...styles.categoryTextWrapper}>
                                    <Typography
                                        {...styles.categoryText}
                                        color="text.main"
                                        size={16}
                                        weight="normal"
                                        lineHeight="20px"
                                    >
                                        <StyledWrapper className="popup-content-wrapper">
                                            {category["shortDescription"]}
                                        </StyledWrapper>
                                    </Typography>
                                </StyledWrapper>
                            </Link>
                        </GridItem>
                    ))}
                </GridContainer>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ShopByCategoryWidget),
    definition,
};

export default widgetModule;
